import { useState } from 'react';
import { Package, Receipt, TrendingUp, Warehouse } from 'lucide-react';

import ChartCard from '@/features/reports/components/ChartCard';
import RankBarChart from '@/features/reports/components/charts/RankBarChart';
import { SimpleKpi } from '@/features/reports/components/KpiTile';
import ReportTable from '@/features/reports/components/ReportTable';
import { useReportOutlet } from '@/features/reports/hooks/useReportOutlet';
import { useTopProducts } from '@/features/reports/hooks/useReports';
import {
  formatNumber,
  formatPercent,
  formatRangeCaption,
  formatToman,
} from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';

const productDetailsLabel = (row) =>
  [row.brand, ...(row.categories ?? [])]
    .map((value) => persianDisplayText(value, ''))
    .filter(Boolean)
    .join(' • ') || '—';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const chartRows = (rows) =>
  rows.map((row) => ({
    ...row,
    label: persianDisplayText(row.name_fa, 'نام ثبت‌نشده'),
  }));

/** Best-selling products by revenue or by units sold. */
export default function ProductsReport() {
  const { query, filters } = useReportOutlet();
  const [orderBy, setOrderBy] = useState('revenue');
  const [limit, setLimit] = useState(20);

  const products = useTopProducts(query, { orderBy, limit });
  const list = products.data?.rows ?? [];

  const listedRevenue = list.reduce((sum, row) => sum + Number(row.revenue ?? 0), 0);
  const listedUnits = list.reduce((sum, row) => sum + Number(row.units ?? 0), 0);
  const topRow = list[0];

  return (
    <div className="reports-page">
      <div className="report-section-head">
        <h2 className="section-title">پرفروش‌ترین کالاها</h2>
        <p className="report-section-caption">
          {formatRangeCaption(filters.from, filters.to)} • مرتب‌شده بر پایه{' '}
          {orderBy === 'revenue' ? 'درآمد' : 'تعداد فروش'}
        </p>
      </div>

      <div className="kpi-grid report-kpi-grid">
        <SimpleKpi
          title="درآمد کل کالاها"
          value={products.data?.total_revenue}
          format={formatToman}
          icon={TrendingUp}
          accent="green"
        />
        <SimpleKpi
          title="درآمد کالاهای فهرست‌شده"
          value={listedRevenue}
          format={formatToman}
          icon={Package}
          accent="blue"
        />
        <SimpleKpi
          title="کالای فروش‌رفته در فهرست"
          value={listedUnits}
          format={formatNumber}
          icon={Receipt}
          accent="purple"
        />
        <SimpleKpi
          title="موجودی کالای نخست"
          value={topRow?.stock_quantity}
          format={formatNumber}
          icon={Warehouse}
          accent="amber"
          secondary={persianDisplayText(topRow?.name_fa)}
        />
      </div>

      <ChartCard
        title={orderBy === 'revenue' ? 'رتبه‌بندی بر پایه درآمد' : 'رتبه‌بندی بر پایه تعداد فروش'}
        subtitle="کالاهای برتر در بازه انتخابی"
        actions={
          <div className="report-card-actions-group">
            <div className="report-segmented">
              <button
                type="button"
                className={`report-segment${orderBy === 'revenue' ? ' active' : ''}`}
                onClick={() => setOrderBy('revenue')}
              >
                درآمد
              </button>
              <button
                type="button"
                className={`report-segment${orderBy === 'units' ? ' active' : ''}`}
                onClick={() => setOrderBy('units')}
              >
                تعداد فروش
              </button>
            </div>
            <select
              className="report-select"
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value))}
              aria-label="تعداد کالا"
            >
              {LIMIT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {formatNumber(option)} کالا
                </option>
              ))}
            </select>
          </div>
        }
        height={420}
        isLoading={products.isLoading}
        isError={products.isError}
        error={products.error}
        isEmpty={!list.length}
      >
        <RankBarChart
          rows={chartRows(list)}
          valueKey={orderBy}
          money={orderBy === 'revenue'}
          height={420}
          yAxisWidth={160}
        />
      </ChartCard>

      <ChartCard
        title="جدول کالاهای برتر"
        subtitle="درآمد، تعداد فروش، سفارش و موجودی هر کالا"
        bodyClassName="report-card-body-table"
        isLoading={products.isLoading}
        isError={products.isError}
        error={products.error}
        isEmpty={!list.length}
      >
        <ReportTable
          rows={list}
          rowKey={(row) => row.product_id}
          maxHeight={520}
          columns={[
            {
              key: 'rank',
              header: '#',
              width: 48,
              render: (_row, index) => formatNumber(index + 1),
            },
            {
              key: 'name',
              header: 'کالا',
              render: (row) => (
                <div className="report-cell-stack">
                  <span className="report-cell-primary">{persianDisplayText(row.name_fa, 'نام ثبت‌نشده')}</span>
                  <span className="report-cell-secondary">{productDetailsLabel(row)}</span>
                </div>
              ),
            },
            { key: 'revenue', header: 'درآمد', render: (row) => formatToman(row.revenue) },
            { key: 'units', header: 'تعداد فروش', render: (row) => formatNumber(row.units) },
            { key: 'orders', header: 'سفارش', render: (row) => formatNumber(row.orders) },
            {
              key: 'stock_quantity',
              header: 'موجودی',
              render: (row) => (
                <span className={row.stock_quantity <= 0 ? 'report-cell-danger' : undefined}>
                  {formatNumber(row.stock_quantity)}
                </span>
              ),
            },
            {
              key: 'share',
              header: 'سهم از فهرست',
              render: (row) =>
                formatPercent(listedRevenue ? (Number(row.revenue ?? 0) / listedRevenue) * 100 : 0),
            },
            {
              key: 'flags',
              header: 'ویژگی',
              render: (row) =>
                row.is_perishable ? <span className="badge badge-amber">فاسدشدنی</span> : '—',
            },
          ]}
        />
      </ChartCard>
    </div>
  );
}
